import { call, put, takeEvery, select, delay } from "redux-saga/effects";

import { takeBuffered } from "./effects/bufferedChannel";
import { RequestStatusesState } from "../reducers/requestStatusesReducer";

/*******************************************/
/* API Request Action/Saga Creator Utility */
/*******************************************/

// SagaAction
//
// All of the dispatched Actions from this file are of the type SagaAction.
// These are reduced by the requestsStatusesReducer to track loading booleans, loadedAt timestampes, and errors.
// It also includes the args that the original Action was called with.

export type SagaAction = {
  type: string;
  requestKey: string;
  args: { [key: number | string]: any };
  timestamp?: number;
  payload?: { response: { [key: number | string]: any } };
  error?: Boolean;
  skipErrorToast?: Boolean;
  resolve: (response: any) => void;
  reject: (error: any) => void;
  retries?: number;
};

// AsyncSagaAction
//
// A Thunk Action with a getRequestKey method.
// It has to be a Thunk so that we can skip dispatching even a loading action when skipping a duplicate
// action with the skipDuplicates option.

export type AsyncSagaAction<ArgsType> = {
  (args: ArgsType): (
    dispatch: (action: SagaAction) => void,
    getState: () => any
  ) => Promise<any>;
  getRequestKey: (args: ArgsType) => string;
};

export type AsyncSagaActionNoArgs = {
  (): (
    dispatch: (action: SagaAction) => void,
    getState: () => any
  ) => Promise<any>;
  getRequestKey: () => string;
};

// createAsyncSaga()
//
// Returns an Action of the AsyncSagaAction type which starts an API Request Saga.
// The generated Saga is added to allAsyncSagas, which is used by generateAsyncSagas() to automatically
// add all the generated Sagas to the Redux Store.
//
// Required:
//     REQUEST, SUCCESS, and FAILURE Action Constants
//     getRequestKey()
//         A function that returns a string which must be unique to the action and the args it was called with.
//     request
//
// Options:
//     skipDuplicates <Boolean> default = true
//         When set to true the Action will not start the Saga if it is already running with the same args.
//     skipErrorToast <Boolean> default = false
//         When set to true the Action will not show the error toast, allowing the user to handle errors in a catch block.
//     loadedAtQueryParam <string> default = undefined
//         When a string is provided and the Saga has been loaded already the timestamp of the last
//         completion of the Saga will be included in the request args. The value of loadedAtQueryParam
//         is used as the requestKey for the timestamp.

let sagasGenerated = false;
const allAsyncSagas: Array<() => Generator> = [];

class AsyncActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AsyncActionError";
  }
}

class BadConfigurationError extends AsyncActionError {
  constructor(message: string) {
    super(message);
    this.name = "BadConfigurationError";
  }
}

export class AlreadyLoadingError extends AsyncActionError {
  constructor(message: string) {
    super(message);
    this.name = "AlreadyLoadingError";
  }
}

export class StateConditionFailedError extends AsyncActionError {
  constructor(message: string) {
    super(message);
    this.name = "StateConditionFailedError";
  }
}

export const createAsyncSaga = (
  REQUEST_CONSTANT: string,
  SUCCESS_CONSTANT: string,
  FAILURE_CONSTANT: string,
  getRequestKey: (args: any) => string,
  request: (args: any) => Promise<any>,
  options?: {
    skipErrorToast?: boolean;
    skipDuplicates?: Boolean;
    loadedAtQueryParam?: string;
    stateConditionFn?: (state: any, args: any) => Boolean;
    bufferDuplicates?: boolean;
    retries?: number;
    retryDelay?: number;
  }
): AsyncSagaAction<Object> | AsyncSagaActionNoArgs => {
  if (sagasGenerated)
    throw new BadConfigurationError(
      `AsyncSaga for ${REQUEST_CONSTANT} was created after saga generation.
       Don't forget to Import files which create Sagas into sagas.ts/rootSaga.ts`
    );

  // Get options
  const skipDuplicates =
    options?.skipDuplicates !== undefined ? options.skipDuplicates : true;
  const loadedAtQueryParam = options?.loadedAtQueryParam;
  const stateConditionFn = options?.stateConditionFn;
  const bufferDuplicates = options?.bufferDuplicates;
  const retries = options?.retries !== undefined ? options.retries : 0;
  const retryDelay = options?.retryDelay !== undefined ? options.retryDelay : 0;
  const skipErrorToast = options?.skipErrorToast;

  if (skipDuplicates && bufferDuplicates)
    throw new BadConfigurationError(
      `You probably don't intend to both skip _and_ buffer ${REQUEST_CONSTANT} duplicates.
       By definition you cannot buffer requestKey duplicates if they're skipped.`
    );

  // Create the Action which starts the Saga
  const requestAction: AsyncSagaAction<Object> | AsyncSagaActionNoArgs =
    (args: Object) => (dispatch, getState) => {
      const requestKey = getRequestKey(args);
      const state = getState();

      if (stateConditionFn && !stateConditionFn(state, args)) {
        return Promise.reject(
          new StateConditionFailedError(
            `state condition failed for ${requestKey}`
          )
        );
      }

      if (skipDuplicates) {
        const alreadyLoading = state.requestStatuses.loading[requestKey];

        if (alreadyLoading) {
          return Promise.reject(
            new AlreadyLoadingError(`already loading ${requestKey}`)
          );
        }
      }

      // TODO: type this Promise<any>
      return new Promise<any>((resolve, reject) => {
        dispatch({
          type: REQUEST_CONSTANT,
          requestKey,
          args,
          resolve,
          reject,
          retries,
          skipErrorToast,
        });
      });
    };

  requestAction.getRequestKey = getRequestKey;

  // Create the Saga which calls the request and dispatches results
  function* sagaAction(action: SagaAction): Generator {
    const { requestKey, skipErrorToast } = action;
    const args = action.args as any;

    if (loadedAtQueryParam) {
      const loadedAt = yield select(
        ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
          requestStatuses.loadedAt[requestKey]
      );
      if (loadedAt) args[loadedAtQueryParam] = loadedAt;
    }

    try {
      const response = yield call(request, args);
      yield put({
        type: SUCCESS_CONSTANT,
        requestKey,
        args,
        payload: { response },
      });
      action.resolve(response);
    } catch (error) {
      if (action.retries) {
        if (retryDelay) yield delay(retryDelay);
        yield put({ ...action, retries: action.retries - 1 });
      } else {
        yield put({
          type: FAILURE_CONSTANT,
          requestKey,
          args,
          payload: { error },
          error: skipErrorToast ? false : true,
        });
        action.reject(error);
      }
    }
  }

  // Add the Saga to allAsyncSagas so that it will be added to the Redux Store
  function* saga(): Generator {
    if (bufferDuplicates) {
      // Buffer requests by requestKey
      // Similar to takeEvery, but holds at most 1 request in a sliding buffer
      yield takeBuffered(REQUEST_CONSTANT, sagaAction);
    } else {
      yield takeEvery(REQUEST_CONSTANT, sagaAction);
    }
  }

  allAsyncSagas.push(saga);

  return requestAction;
};

// generateAsyncSagas()
//
// The function used to add all the generated Sagas to the Redux Store

export const generateAsyncSagas = () => {
  sagasGenerated = true;
  return allAsyncSagas.map((saga: () => Generator) => saga());
};

// Testing Only Functions

// getAllAsyncSagas() is for tests only
export const getAllAsyncSagas = () => allAsyncSagas;

// addAsyncSaga() is for tests only
export const addAsyncSaga = (saga: () => Generator) => allAsyncSagas.push(saga);

// resetAllAsyncSagas() is for tests only
export const resetAllAsyncSagas = () => {
  allAsyncSagas.length = 0;
  sagasGenerated = false;
};
